/**
 * Product & Pricing Types for Stripe Integration
 *
 * Products represent purchasable items (membership dues, VIP upgrades, parking, children tickets).
 * Each product has tiered pricing where the price depends on how many have been sold.
 *
 * Data Model:
 * - products/{productId} - Product configuration with pricing tiers
 * - Each product syncs to a Stripe Product with multiple Stripe Prices (one per tier)
 * - Products are scoped to a parade year
 */

import type { FieldValue, Timestamp } from 'firebase/firestore'
import type { MemberType } from './memberType.js'

/**
 * Product type categories
 */
export type ProductType = 'membership' | 'vip' | 'parking' | 'children'

/**
 * A pricing tier within a product
 * Tiers are evaluated in order - when quantitySold exceeds upToQuantity,
 * the next tier becomes active
 */
export interface PricingTier {
  id: string
  stripePriceId: string | null // Stripe Price ID (null if not yet synced)
  unitAmount: number // Price in cents (6000 = $60.00)
  upToQuantity: number | null // Cumulative limit; null = unlimited
  label: string // Display name: "Early Bird", "Regular", etc.
}

/**
 * Product stored in Firestore
 * Path: products/{productId}
 */
export interface ProductBase {
  id: string // e.g., "membership-2027"
  stripeProductId: string | null // Stripe Product ID (null if not yet synced)

  // A second Stripe Product used only for the discounted portion of a bulk
  // order. A Checkout line's display name always comes from its product and
  // cannot be overridden per line, so putting both portions on one product made
  // Stripe render two identically-named rows — which reads as a single
  // membership at the lower price. Null until the product is synced.
  stripeDiscountedProductId?: string | null
  name: string // "2027 Parade Membership"
  description: string
  type: ProductType
  year: string // Parade year (e.g., "2027")
  active: boolean // Whether product can be purchased

  // Tiered pricing (ordered by upToQuantity ascending)
  pricingTiers: PricingTier[]

  // Availability window
  salesStart: Date | Timestamp | null // null = immediately available
  salesEnd: Date | Timestamp | null // null = no end date

  // Inventory limits
  maxQuantity: number | null // Total quantity available; null = unlimited

  // Eligibility & Prerequisites
  termsMessage: string | null // Custom terms user must agree to (e.g., parking lot warning)
  requiresProducts: ProductType[] // Other products member must have purchased first (e.g., VIP requires membership)
  eligibleMemberTypes: MemberType[] | null // Member types that can purchase; null = all types
  excludedMemberTypes: MemberType[] // Member types blocked from purchasing (e.g., redshirts)

  // Sales tracking
  quantitySold: number // Incremented on purchase, decremented on refund

  // Audit
  copiedFrom?: string // ID of product this was copied from
  created_at: Date | Timestamp | FieldValue
  updated_at: Date | Timestamp | FieldValue
  createdBy: string // UID of admin who created
  updatedBy?: string // UID of admin who last updated
}

/**
 * Product with resolved dates (for use in app)
 */
export interface Product extends Omit<ProductBase, 'salesStart' | 'salesEnd' | 'created_at' | 'updated_at'> {
  salesStart: Date | null
  salesEnd: Date | null
  created_at: Date
  updated_at: Date
}

/**
 * Firestore data format
 */
export interface ProductFirestoreData extends Omit<ProductBase, 'salesStart' | 'salesEnd' | 'created_at' | 'updated_at'> {
  salesStart: Timestamp | null
  salesEnd: Timestamp | null
  created_at: Timestamp | FieldValue
  updated_at: Timestamp | FieldValue
}

/**
 * Input for creating a new product
 */
export interface CreateProductInput {
  name: string
  description: string
  type: ProductType
  year: string
  pricingTiers: Omit<PricingTier, 'stripePriceId'>[]
  salesStart?: Date | null
  salesEnd?: Date | null
  maxQuantity?: number | null
  termsMessage?: string | null
  requiresProducts?: ProductType[]
  eligibleMemberTypes?: MemberType[] | null
  excludedMemberTypes?: MemberType[]
}

/**
 * Input for updating a product
 */
export interface UpdateProductInput {
  name?: string
  description?: string
  active?: boolean
  pricingTiers?: PricingTier[]
  salesStart?: Date | null
  salesEnd?: Date | null
  maxQuantity?: number | null
  termsMessage?: string | null
  requiresProducts?: ProductType[]
  eligibleMemberTypes?: MemberType[] | null
  excludedMemberTypes?: MemberType[]
}

/**
 * Input for copying products to a new year
 */
export interface CopyProductsInput {
  sourceYear: string
  targetYear: string
  productIds?: string[] // Specific products to copy; omit for all
}

/**
 * Summary of a product's current pricing state
 */
export interface ProductPricingSummary {
  productId: string
  currentTier: PricingTier
  currentPrice: number // In cents
  quantitySold: number
  quantityRemaining: number | null // null if unlimited
  nextTierAt: number | null // Quantity at which price increases
  isAvailable: boolean // salesStart/End + active + inventory check
}

/**
 * Get the current active pricing tier for a product
 */
export function getCurrentTier(product: Product): PricingTier {
  const sold = product.quantitySold

  for (const tier of product.pricingTiers) {
    if (tier.upToQuantity === null || sold < tier.upToQuantity) {
      return tier
    }
  }

  // Fallback to last tier (should have upToQuantity: null)
  const lastTier = product.pricingTiers[product.pricingTiers.length - 1]
  if (!lastTier) {
    // Every caller assumes a tier exists, checkout included. Failing here
    // names the offending product instead of surfacing later as
    // `undefined.unitAmount` somewhere down the Stripe path.
    throw new Error(`Product ${product.id} has no pricing tiers`)
  }
  return lastTier
}

/**
 * Check if a product is currently available for purchase
 */
export function isProductAvailable(product: Product): boolean {
  if (!product.active) return false

  const now = new Date()

  if (product.salesStart && now < product.salesStart) return false
  if (product.salesEnd && now > product.salesEnd) return false

  // Check inventory
  if (product.maxQuantity !== null && product.quantitySold >= product.maxQuantity) {
    return false
  }

  return true
}

/**
 * Get remaining quantity for a product
 */
export function getRemainingQuantity(product: Product): number | null {
  if (product.maxQuantity === null) return null
  return Math.max(0, product.maxQuantity - product.quantitySold)
}

/**
 * Per-member purchase limits by product type
 * - membership: 1 per member per year
 * - vip: 1 per member per year
 * - children: up to the number of children the member has
 * - parking: unlimited
 */
export const MEMBER_PURCHASE_LIMITS: Record<ProductType, number | 'children' | 'unlimited'> = {
  membership: 1,
  vip: 1,
  children: 'children', // Dynamic limit based on member's children count
  parking: 'unlimited',
}

/**
 * Result of checking member eligibility for a product
 */
export interface EligibilityResult {
  eligible: boolean
  reason?: 'member_type_excluded' | 'member_type_not_eligible' | 'missing_prerequisite' | 'product_unavailable' | 'purchase_limit_reached'
  details?: string
  missingProducts?: ProductType[]
  maxQuantity?: number // Max purchasable quantity for this member
  currentQuantity?: number // How many they already have
}

/**
 * Member's purchase counts for eligibility checking
 */
export interface MemberPurchaseCounts {
  membership: number
  vip: number
  children: number
  parking: number
}

/**
 * Check if a member is eligible to purchase a product based on their type and purchases
 *
 * @param product - The product to check
 * @param memberType - The member's type
 * @param purchaseCounts - How many of each product type the member has purchased this year
 * @param childrenCount - Number of children the member has (for children ticket limits)
 */
export function getMemberEligibility(
  product: Product,
  memberType: MemberType,
  purchaseCounts: MemberPurchaseCounts,
  childrenCount: number = 0,
): EligibilityResult {
  // Check excluded member types
  if (product.excludedMemberTypes.includes(memberType)) {
    return {
      eligible: false,
      reason: 'member_type_excluded',
      details: `${memberType} members cannot purchase ${product.name}`,
    }
  }

  // Check eligible member types (null = all types eligible)
  if (product.eligibleMemberTypes !== null && !product.eligibleMemberTypes.includes(memberType)) {
    return {
      eligible: false,
      reason: 'member_type_not_eligible',
      details: `This product is not available to ${memberType} members`,
    }
  }

  // Check prerequisite products
  if (product.requiresProducts.length > 0) {
    const ownedProductTypes = Object.entries(purchaseCounts)
      .filter(([_, count]) => count > 0)
      .map(([type]) => type as ProductType)
    const missing = product.requiresProducts.filter(req => !ownedProductTypes.includes(req))
    if (missing.length > 0) {
      return {
        eligible: false,
        reason: 'missing_prerequisite',
        details: `Requires ${missing.join(', ')} purchase first`,
        missingProducts: missing,
      }
    }
  }

  // Check per-member purchase limits
  const limit = MEMBER_PURCHASE_LIMITS[product.type]
  const currentQuantity = purchaseCounts[product.type] || 0
  let maxQuantity: number

  if (limit === 'unlimited') {
    maxQuantity = Infinity
  }
  else if (limit === 'children') {
    maxQuantity = childrenCount
  }
  else {
    maxQuantity = limit
  }

  if (currentQuantity >= maxQuantity) {
    let details: string
    if (product.type === 'membership') {
      details = 'You already have a membership for this year'
    }
    else if (product.type === 'vip') {
      details = 'You already have a VIP ticket for this year'
    }
    else if (product.type === 'children') {
      details = childrenCount === 0
        ? 'You have no children registered'
        : 'You already have tickets for all your children'
    }
    else {
      details = 'You have reached the purchase limit for this product'
    }

    return {
      eligible: false,
      reason: 'purchase_limit_reached',
      details,
      maxQuantity: maxQuantity === Infinity ? undefined : maxQuantity,
      currentQuantity,
    }
  }

  // Check product availability (active, dates, inventory)
  if (!isProductAvailable(product)) {
    return {
      eligible: false,
      reason: 'product_unavailable',
      details: 'Product is not currently available',
    }
  }

  return {
    eligible: true,
    maxQuantity: maxQuantity === Infinity ? undefined : maxQuantity,
    currentQuantity,
  }
}

/**
 * Legacy helper for backward compatibility - converts ownedProductTypes to purchaseCounts
 * @deprecated Use getMemberEligibility with purchaseCounts instead
 */
export function getMemberEligibilitySimple(
  product: Product,
  memberType: MemberType,
  ownedProductTypes: ProductType[],
): EligibilityResult {
  const purchaseCounts: MemberPurchaseCounts = {
    membership: ownedProductTypes.includes('membership') ? 1 : 0,
    vip: ownedProductTypes.includes('vip') ? 1 : 0,
    children: ownedProductTypes.includes('children') ? 1 : 0,
    parking: ownedProductTypes.includes('parking') ? 1 : 0,
  }
  return getMemberEligibility(product, memberType, purchaseCounts, 0)
}
